import { useTranslation } from "react-i18next";
import TabsComponent from "../../../../components/tabs/TabsComponent";
import { useParams, useSearchParams } from "react-router-dom";
import StudentCard from "../../../../components/student-card";
import { makeApiRequest } from "../../../../helpers";
import {
  indexStudent,
  indexStudentSchedules,
  indexStudentSkills,
} from "../../../../api/endpoints/supervisors/students";
import { ApiResponse } from "../../../../api/apiResponse";
import StudentModel from "../../../../api/models/StudentModel";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import StudentSkills from "../../../../components/student-info/StudentSkills";
import StudentNotes from "../../../../components/student-info/StudentNotes";
import { StudentProfile } from "../../../../components/student-info/StudentProfile";
import CandidateCourseModel from "../../../../api/models/CandidateCourseModel";
import { StatisticsType } from "../../../../api/@types/scheduleTypes";
import { sortAndCleanQuranProgress } from "../../../../helpers/statistics";
import { GetCurrentWindowSize } from "../../../../hooks/useWindowSize";
import FormattedScheduleResponseModel from "../../../../api/models/FormattedScheduleResponseModel";
import { FullSpinner } from "../../../../components/LoadingSpinner";

export default function PersonalStudentInfoPage() {
  const currentWindow = GetCurrentWindowSize();
  const LocalBasePath = "page.supervisor.student";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [student, setStudent] = useState<StudentModel | null>(null);
  const { id } = useParams();
  const [sessionsNotes, setSessionsNotes] = useState<{
    passedNotes: number;
    unpassedNotes: number;
    sessionIds: string[];
  }>({ passedNotes: 0, unpassedNotes: 0, sessionIds: [] });
  const [courses, setCourses] = useState<CandidateCourseModel[]>([]);
  const [quranProgress, setQuranProgress] = useState<any>(null);
  const [sessionTardiness, setSessionTardiness] = useState([]);
  const [quranProgressSessions, setQuranProgressSessions] = useState<any>(null);
  const [reservedSchedule, setReservedSchedule] =
    useState<FormattedScheduleResponseModel | undefined>();
  const [skillsStatistics, setSkillsStatistics] = useState<{
    passed: number;
    total: number;
    unPassed: number;
  }>({ unPassed: 0, total: 0, passed: 0 });
  const [quranPagesProgress, setQuranPagesProgress] = useState<{
    finished: number;
    remaining: number;
  }>({ finished: 0, remaining: 644 });
  const [sessionsStatistics, setSessionsStatistics] = useState<{
    scheduled: number;
    completed: number;
    canceled: number;
  }>({
    completed: 0,
    scheduled: 0,
    canceled: 0,
  });
  const { data: finishedSkills } = useQuery(
    ["skills", id],
    () => {
      return indexStudentSkills(id ?? "");
    },
    {
      select: (response) => {
        let skills: string[] | undefined = undefined;
        if (response.isSuccess) {
          skills = response.data?.skills;
        }
        return skills;
      },
    }
  );

  const fetchSchedule = async () => {
    await makeApiRequest(
      () => indexStudentSchedules(id!),
      (res: ApiResponse) => {
        const schedule = FormattedScheduleResponseModel.fromApiResponse(res);
        setReservedSchedule(schedule);
      }
    );
  };

  useEffect(() => {
    if (!!id) fetchSchedule();
  }, [id]);

  const getStudentInfo = () => {
    makeApiRequest(
      () => indexStudent(id ?? ""),
      (res: ApiResponse) => {
        const studentInfo = StudentModel.fromApiResponse(res.data?.student);
        setStudent(studentInfo);
        const { learn_dash_progresses }: StatisticsType = res.data;

        const {
          notes_statistics,
          quran_pages_statistics,
          session_status_statistics,
          session_tardiness,
          quran_progress,
          skills_statistics,
        }: StatisticsType = res.data.statistics;
        const ListCourses = CandidateCourseModel.fromApiResponse(
          learn_dash_progresses
        );
        //quranProgress start here
        const statisticsPages: number[] = [];
        const statisticsSessions: string[] = [];
        const sortedBySessionsQuranProgress: { [key: string]: number }[] =
          sortAndCleanQuranProgress(
            quran_progress,
            currentWindow === "sm" ? 5 : 10
          );
        sortedBySessionsQuranProgress.forEach((page: any) => {
          for (let key in page) {
            statisticsSessions.push(key);
            statisticsPages.push(+(page[key] ?? 0));
          }
        });
        setQuranProgress(statisticsPages);
        setQuranProgressSessions(statisticsSessions);
        setCourses(ListCourses);
        setSessionTardiness(session_tardiness);
        setQuranPagesProgress({
          finished: quran_pages_statistics.total_read_pages,
          remaining:
            quran_pages_statistics.total_pages -
            quran_pages_statistics.total_read_pages,
        });
        setSessionsStatistics({
          scheduled: session_status_statistics?.scheduled,
          completed: session_status_statistics?.completed,
          canceled: session_status_statistics?.canceled,
        });
        let passedNotes = 0;
        if (notes_statistics.passed_notes_counts?.length) {
          notes_statistics.passed_notes_counts.forEach((count) => {
            passedNotes += count;
          });
        }
        let unpassedNotes = 0;
        if (notes_statistics.failed_notes_counts?.length) {
          notes_statistics.failed_notes_counts.forEach((count) => {
            unpassedNotes += count;
          });
        }
        setSessionsNotes({
          passedNotes: passedNotes,
          unpassedNotes: unpassedNotes,
          sessionIds: [...Array(notes_statistics.total_sessions_count)].map(
            (_, i) => `#${i + 1}`
          ),
        });
        setSkillsStatistics({
          passed: skills_statistics?.passed_skills_count,
          total: skills_statistics?.total_skills_count,
          unPassed: skills_statistics?.total_skills_count,
        });
        setIsLoading(false);
      }
    );
  };
  const tabs = [
    {
      name: t("info"),
      content: (
        <StudentProfile
          quranPagesProgress={quranPagesProgress}
          quranProgress={quranProgress}
          courses={courses}
          student={student}
          sessionsNotes={sessionsNotes}
          skillsStatistics={skillsStatistics}
          quranProgressSessions={quranProgressSessions}
          sessionStatistics={sessionsStatistics}
          statistics={{
            session_tardiness: sessionTardiness,
          }}
          scheduleSlots={reservedSchedule?.schedule ?? {}}
        />
      ),
    },
    {
      name: t("skills"),
      content: <StudentSkills finishedSkills={finishedSkills || []} />,
    },
    {
      name: t("notes"),
      content: <StudentNotes />,
    },
  ];
  useEffect(() => {
    if (!!id) getStudentInfo();
  }, [id]);
  return (
    <>
      {isLoading ? (
        <div className="w-full h-full bg-white">
          <FullSpinner size={64} />
        </div>
      ) : (
        <div className="flex flex-col w-full min-h-full gap-8 bg-gray-50">
          <div className="px-6 pt-6">
            <StudentCard showKhetma={false} student={student} />
          </div>
          <TabsComponent
            requests={true}
            defaultTab={+(searchParams.get("tab") ?? 0)}
            tabs={tabs}
          />
        </div>
      )}
    </>
  );
}
